import React from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { Details } from '@dmm/lib-react-ui-components';
import {
  getFeaturesData,
  getPropulsionData,
  getMeasurementsData,
  formatMoreDetailsDescriptions,
  formatMainDescription
} from '../../../utils/listingHelper';
import DataHtml from './components/DataHtml';
import DataDetails from './components/DataDetails';
import ConversationalCta from '../../../components/ConversationalCta';
import { useIntl } from 'react-intl';
import { getMessages } from '../../../tppServices/translations/messages';
import {getLanguageCode } from '../../../utils/language';
import { asArray } from '../../../utils';

const ConditionalRender = ({
  data,
  title,
  onClick,
  isHtml = false,
  isPricing = false,
  isMultiItem = false,
  extraRenderStatement = true,
  conversationalCta = false,
  onOpenContactForm,
  icon,
  id,
  titleCTA,
  contentCTA,
  isOpen = false,
  showInAccordionConversionalCTA = false
}) => {
  if (isEmpty(data) || data?.length === 0 || !extraRenderStatement) {
    return null;
  }

  const hasSublevel = typeof data[0]?.value !== 'string';

  return (
    <Details heading={title} onClick={onClick} isOpen={isOpen}>
      {isHtml
        ? <DataHtml data={data}
            isMultiItem={isMultiItem}
            showConversionalCTA={showInAccordionConversionalCTA}
            icon={icon}
            onOpenContactForm={onOpenContactForm}
            title={titleCTA}
            content={contentCTA}
            id={id}
            elementSelector = {'.accordion-details-items'}
          />
        : <DataDetails data={data} isPricing={isPricing} isSubLevel={hasSublevel} />
      }
      {conversationalCta &&
        <ConversationalCta
          id={id}
          enabled={true}
          variant="button"
          classNames={{ icon: true }}
          icon={icon}
          onClick={onOpenContactForm}
          title={titleCTA}
          content={contentCTA}
        />
      }
    </Details>
  );
};



const AccordionDetails = ({listing, setCollapseContentTracking, onOpenContactForm, customUom}) => {
  const intl = useIntl();
  const t = intl.formatMessage;
  const currentLocale = intl.locale;
  const currentLanguage = getLanguageCode(currentLocale);
  const messages = getMessages();
  const featuresData = getFeaturesData(listing);
  const propulsionData = getPropulsionData(listing);
  const measurementsData = getMeasurementsData(listing, customUom);
  const mainDescription = formatMainDescription(
    listing.description || '',
    listing.mainDescriptions || [],
    currentLanguage);

  const moreDetails = formatMoreDetailsDescriptions(asArray(listing.descriptions), currentLanguage);
  const salesRep = get(listing, 'salesRep');
  const salesRepPhone = get(salesRep, 'phoneNumbers[0].number');
  const salesRepPhoneLink = `<a href="tel:${salesRepPhone}">${salesRepPhone}</a>`;
  const salesRepData = `${t(messages.contactSalesRep, {
    firstName: get(salesRep, 'firstName'),
    lastName: get(salesRep, 'lastName')
  })} ${salesRepPhoneLink}`;

  return (
    <div className="accordion-details-wrapper">
      <section className="accordion-details-section">
        <h3>{t(messages.socialShare.boatDetailsTitle)}</h3> {/* TODO Add correct translation for every language when available */}
        <div className="accordion-details-items">
          <ConditionalRender
            data={mainDescription}
            title={t(messages.description)}
            isHtml={true}
            onClick={() => {
              setCollapseContentTracking('boat-description');
            }}
            isOpen={true}
            onOpenContactForm={onOpenContactForm}
            conversationalCta={false}
            icon={'description'}
            id={'description-conversational-cta'}
            titleCTA={t(messages.boatDetails.description.conversationalCta.title)}
            contentCTA={t(messages.boatDetails.description.conversationalCta.content)}
            showInAccordionConversionalCTA={true}
          />
          {salesRep && salesRepPhone && (
            <ConditionalRender
              data={salesRepData}
              title={t(messages.boatDetails.friendlyFeatureName.customContactInformation)}
              isHtml={true}
              onClick={() => {
                setCollapseContentTracking('contact-information');
              }}
            />
          )}
          <ConditionalRender
            data={moreDetails}
            title={t(messages.moreDetails)}
            isHtml={true}
            isMultiItem={true}
            onClick={() => {
              setCollapseContentTracking('more-details');
            }}
            onOpenContactForm={onOpenContactForm}
            conversationalCta={true}
            icon={'description'}
            id={'more-details-conversational-cta'}
            titleCTA={t(messages.boatDetails.moreDetails.conversationalCta.title)}
            contentCTA={t(messages.boatDetails.moreDetails.conversationalCta.content)}

          />
          <ConditionalRender
            data={featuresData}
            title={t(messages.features)}
            onClick={() => {
              setCollapseContentTracking('features');
            }}
            onOpenContactForm={onOpenContactForm}
            conversationalCta={true}
            icon={'description'}
            id={'features-conversational-cta'}
            titleCTA={t(messages.boatDetails.features.conversationalCta.title)}
            contentCTA={t(messages.boatDetails.features.conversationalCta.content)}
          />
          <ConditionalRender
            data={propulsionData}
            title={t(messages.propulsion)}
            onClick={() => {
              setCollapseContentTracking('propulsion');
            }}
          />
          <ConditionalRender
            data={measurementsData}
            title={t(messages.measurements)}
            onClick={() => {
              setCollapseContentTracking('measurements');
            }}
            onOpenContactForm={onOpenContactForm}
            conversationalCta={true}
            icon={'measurements'}
            id={'measurements-conversational-cta'}
            titleCTA={t(messages.boatDetails.measurements.conversationalCta.title)}
            contentCTA={t(messages.boatDetails.measurements.conversationalCta.content)}
          />

        </div>
      </section>
    </div>
  );
};

ConditionalRender.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.object
  ]),
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  isHtml: PropTypes.bool,
  isPricing: PropTypes.bool,
  isMultiItem: PropTypes.bool,
  extraRenderStatement: PropTypes.bool,
  conversationalCta: PropTypes.bool,
  onOpenContactForm: PropTypes.func,
  icon: PropTypes.string,
  id: PropTypes.string,
  titleCTA: PropTypes.string,
  contentCTA: PropTypes.string,
  isOpen: PropTypes.bool,
  showInAccordionConversionalCTA: PropTypes.bool
};

AccordionDetails.propTypes = {
  listing: PropTypes.object.isRequired,
  setCollapseContentTracking: PropTypes.func,
  onOpenContactForm: PropTypes.func,
  customUom: PropTypes.object
};


export default AccordionDetails;
